import Head from "next/head";
import { useRouter } from "next/router";
import { useContext } from "react";

import { AppContext, ThemeMode } from "@context/context";
import { FaqItem } from "@lib/blog";
import { BASE_URL, buildLocalizedPageUrls } from "@lib/seoUrls";

export interface PageMetadataProps {
  title: string;
  description: string;
  blogPostDate?: string;
  faq?: FaqItem[] | null;
}

export const PageMetadata = ({
  title,
  description,
  blogPostDate,
  faq,
}: PageMetadataProps) => {
  const appContext = useContext(AppContext);
  const { locale, asPath } = useRouter();

  const { canonicalUrl, enUrl, plUrl, xDefaultUrl } = buildLocalizedPageUrls(
    locale,
    asPath,
  );

  const structuredDataForBlogs = blogPostDate
    ? {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": title,
        description,
        "url": canonicalUrl,
        "mainEntityOfPage": canonicalUrl,
        "datePublished": blogPostDate,
        "author": {
          "@type": "Organization",
          "name": "Blues Brackets",
        },
        "publisher": {
          "@type": "Organization",
          "name": "Blues Brackets",
          "logo": {
            "@type": "ImageObject",
            "url": `${BASE_URL}/favicon-dark.png`,
          },
        },
      }
    : null;

  const faqStructuredData =
    faq && faq.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faq.map(item => ({
            "@type": "Question",
            "name": item.q,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": item.a,
            },
          })),
        }
      : null;

  return (
    <Head>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="viewport" content="initial-scale=1, width=device-width" />
      <link rel="canonical" href={canonicalUrl} />

      {enUrl && <link rel="alternate" hrefLang="en" href={enUrl} />}
      {plUrl && <link rel="alternate" hrefLang="pl" href={plUrl} />}
      {xDefaultUrl && (
        <link rel="alternate" hrefLang="x-default" href={xDefaultUrl} />
      )}

      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={blogPostDate ? "article" : "website"} />
      <meta property="og:image" content={`${BASE_URL}/og-dafault.png`} />

      {structuredDataForBlogs && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({ ...structuredDataForBlogs }),
          }}
        />
      )}

      {faqStructuredData && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(faqStructuredData),
          }}
        />
      )}

      <link
        rel="icon"
        type="image/svg+xml"
        href={
          appContext.systemTheme === ThemeMode.Light
            ? "/favicon-light.svg"
            : "/favicon-dark.svg"
        }
      />
      <link
        rel="icon"
        type="image/png"
        href={
          appContext.systemTheme === ThemeMode.Light
            ? "favicon-light.png"
            : "favicon-dark.png"
        }
      />
      <link
        rel="apple-touch-icon"
        sizes="180x180"
        href="/apple-touch-icon.png"
      />
      <link
        rel="icon"
        type="image/png"
        sizes="32x32"
        href="/favicon-32x32.png"
      />
      <link
        rel="icon"
        type="image/png"
        sizes="16x16"
        href="/favicon-16x16.png"
      />
      <link rel="manifest" href="/site.webmanifest" />
      <link rel="mask-icon" href="/safari-pinned-tab.svg" color="#1a1a1a" />
      <meta name="msapplication-TileColor" content="#1a1a1a" />
      <meta name="theme-color" content="#1a1a1a" />
    </Head>
  );
};
